import vSelect from "vue-select";
import TagModel from "../../../models/TagModel";
import _debounce from 'lodash/debounce';

export default {
	props: {
		context: {
			type: Object,
			required: true
		},
		collection: {
			type: Object,
			required: false,
			default: null,
		},
		tagModel: {
			type: Object
		},
		emptyMessage: {
			type: String
		}
	},

	components: {
		"v-select": vSelect,
	},

	data() {
		return {
			loading: false,
			searchQuery: '',
			searching: false,
		};
	},

	computed: {
		selectedValue() {
			const value = this.context?._value ?? this.context?.value ?? this.context?.model;
			return Array.isArray(value) ? value : [];
		},
		canCreateTag() {
			const query = (this.searchQuery || '').trim();
			return query.length > 0 && !this.searching && !this.hasExactMatch(query);
		},
		tagCollection() {
			return this.readProp('collection');
		},
		tagModelInstance() {
			return this.readProp('tagModel') || this.readProp('tag-model');
		},
		emptyMessageText() {
			return this.readProp('emptyMessage') || this.readProp('empty-message') || '';
		},
		options() {
			const collection = this.tagCollection;
			if (!collection || typeof collection.toArray !== 'function') {
				return [];
			}
			return collection.toArray(true).map((item) => ({
				...item,
				label: item.label || item.title || item.name,
				value: item.value || item.uuid,
			}));
		}
	},

	methods: {
		readProp(name) {
			if (this[name] !== undefined && this[name] !== null) return this[name];
			const context = this.context;
			if (!context) return undefined;
			if (context[name] !== undefined) return context[name];
			if (context.node?.props?.[name] !== undefined) return context.node.props[name];
			if (context.attrs?.[name] !== undefined) return context.attrs[name];
			return undefined;
		},

		handleInput(value) {
			const next = Array.isArray(value) ? value : [];
			if (this.context.node && typeof this.context.node.input === 'function') {
				this.context.node.input(next);
			} else if (this.context.handlers && typeof this.context.handlers.input === 'function') {
				this.context.handlers.input(next);
			}
			this.clearSearch();
		},

		clearSearch() {
			this.searchQuery = '';
			this.$nextTick(() => {
				if (this.$refs.select) this.$refs.select.search = '';
			});
		},

		optionLabels(item) {
			if (item == null) return [];
			if (typeof item !== 'object') return [String(item).toLowerCase()];
			return [item.label, item.name, item.title]
				.filter(Boolean)
				.map((label) => String(label).trim().toLowerCase());
		},

		hasExactMatch(query) {
			const needle = String(query || '').trim().toLowerCase();
			if (!needle) return false;
			return [...this.options, ...this.selectedValue].some((item) => this.optionLabels(item).includes(needle));
		},

		createOption(option) {
			const label = typeof option === 'string' ? option : (option.label || option.name || option);
			return {
				label,
				name: label,
				title: label,
				value: `temp-${label}`,
			};
		},

		onClose() {
			this.clearSearch();
			this.context.handlers?.blur?.();
		},

		onOptionClick(option) {
			const next = this.selectedValue.filter((item) => {
				const itemKey = item.uuid || item.value || item.label;
				const optionKey = option.uuid || option.value || option.label;
				return itemKey !== optionKey;
			});
			this.handleInput(next);
		},

		onSearch(val, loading) {
			this.searchQuery = val || '';
			this.searching = !!(val && val.length);
			this.fetchOptions(val, loading);
		},

		fetchOptions: _debounce(function (val, loading) {
			if (!val || !val.length) {
				this.searching = false;
				loading(false);
				return;
			}
			const collection = this.tagCollection;
			if (!collection || typeof collection.fetch !== 'function') {
				this.searching = false;
				loading(false);
				return;
			}
			loading(true);
			collection.setConfig('params', Object.assign({}, collection.config?.params || {}, { query: val }));
			collection.fetch().then(() => {
				this.searching = false;
				loading(false);
			}).catch(() => {
				this.searching = false;
				loading(false);
			});
		}, 500),

		onAddTag(val) {
			const tagModel = this.tagModelInstance;
			const collection = this.tagCollection;
			if (!tagModel || typeof tagModel.clone !== 'function') return;

			this.loading = true;
			const newTaxo = tagModel.clone(true).setConfig(tagModel.config) || new TagModel();
			const label = val.label || val.name || val;
			newTaxo.set({ name: label, title: label, label });
			newTaxo.post().then(() => {
				if (collection && typeof collection.set === 'function') {
					collection.set(newTaxo);
				}
				const created = {
					label: newTaxo.label || newTaxo.title || newTaxo.name || label,
					title: newTaxo.title || newTaxo.name || label,
					name: newTaxo.name || newTaxo.title || label,
					uuid: newTaxo.uuid,
					value: newTaxo.uuid || newTaxo.value,
				};
				const next = this.selectedValue
					.filter((item) => (item.value || item.label) !== (val.value || val.label))
					.concat(created);
				this.handleInput(next);
				this.loading = false;
			}).catch(() => {
				this.handleInput(this.selectedValue.filter((item) => (item.value || item.label) !== (val.value || val.label)));
				this.loading = false;
			});
		}
	},
};
